import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { MapPin, Tag, Search, X as XIcon } from "lucide-react";
import {
    checkOrderAddressLocation,
    updateOrderDeliveryAddress,
} from "../../service/api/orders";

const DEFAULT_ZOOM = 15;
const DEBOUNCE_MS = 600;
const SEARCH_DEBOUNCE_MS = 350;

const pinSvg = `
<svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 36 44">
  <filter id="dsa"><feDropShadow dx="0" dy="2" stdDeviation="2.5" flood-opacity="0.25"/></filter>
  <ellipse cx="18" cy="41" rx="7" ry="3" fill="rgba(0,0,0,0.15)"/>
  <path d="M18 2C10.3 2 4 8.3 4 16c0 10.5 14 26 14 26S32 26.5 32 16C32 8.3 25.7 2 18 2z" fill="#6366f1" filter="url(#dsa)"/>
  <circle cx="18" cy="16" r="7.5" fill="white"/>
  <circle cx="18" cy="14" r="3" fill="#6366f1"/>
  <path d="M11 22c0-3.5 3.1-5 7-5s7 1.5 7 5" fill="#6366f1"/>
</svg>`;

const dragPinIcon = L.divIcon({
    html: pinSvg,
    className: "",
    iconSize: [40, 50],
    iconAnchor: [20, 50],
});

const fmt = (v) => `₹${Number(v ?? 0).toFixed(2)}`;

const getInitialCoords = (order) => {
    try {
        const loc = JSON.parse(order?.location || "{}");

        const lat = Number(loc?.lat ?? order?.restaurant?.latitude);
        const lng = Number(loc?.lng ?? order?.restaurant?.longitude);

        return {
            lat: Number.isFinite(lat) ? lat : 0,
            lng: Number.isFinite(lng) ? lng : 0,
        };
    } catch {
        const lat = Number(order?.restaurant?.latitude);
        const lng = Number(order?.restaurant?.longitude);

        return {
            lat: Number.isFinite(lat) ? lat : 0,
            lng: Number.isFinite(lng) ? lng : 0,
        };
    }
};

const ChangeAddressModal = ({ order, onClose, onUpdated, showToast }) => {
    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markerRef = useRef(null);
    const debounceRef = useRef(null);
    const requestSeq = useRef(0);

    const searchDebounceRef = useRef(null);
    const searchAbortRef = useRef(null);
    const searchWrapRef = useRef(null);

    const [coords, setCoords] = useState(() => getInitialCoords(order));
    const [addressText, setAddressText] = useState(order?.address || "");
    const [checking, setChecking] = useState(false);
    const [saving, setSaving] = useState(false);
    const [locationInfo, setLocationInfo] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [fastExpressPicked, setFastExpressPicked] = useState(false);

    const [searchQuery, setSearchQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [searching, setSearching] = useState(false);
    const [suggestionsOpen, setSuggestionsOpen] = useState(false);
    const [resolvingPlace, setResolvingPlace] = useState(false);

    // ── Close suggestions dropdown on outside click ──────────
    useEffect(() => {
        const handler = (e) => {
            if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
                setSuggestionsOpen(false);
            }
        };

        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // ── Search address using OpenStreetMap Nominatim ──────────
    useEffect(() => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        if (searchAbortRef.current) {
            searchAbortRef.current.abort();
            searchAbortRef.current = null;
        }

        const q = searchQuery.trim();

        if (!q || q.length < 3) {
            setSuggestions([]);
            setSuggestionsOpen(false);
            setSearching(false);
            return;
        }

        setSearching(true);

        searchDebounceRef.current = setTimeout(async () => {
            const controller = new AbortController();
            searchAbortRef.current = controller;

            try {
                const restaurantLat = Number(order?.restaurant?.latitude);
                const restaurantLng = Number(order?.restaurant?.longitude);

                const params = new URLSearchParams({
                    q,
                    format: "jsonv2",
                    addressdetails: "1",
                    limit: "8",
                    countrycodes: "in",
                });

                // Bias results around restaurant location if available.
                if (Number.isFinite(restaurantLat) && Number.isFinite(restaurantLng)) {
                    const delta = 0.45; // roughly 50 km
                    params.set(
                        "viewbox",
                        [
                            restaurantLng - delta,
                            restaurantLat + delta,
                            restaurantLng + delta,
                            restaurantLat - delta,
                        ].join(",")
                    );
                    params.set("bounded", "0");
                }

                const res = await fetch(
                    `https://nominatim.openstreetmap.org/search?${params.toString()}`,
                    {
                        signal: controller.signal,
                        headers: {
                            Accept: "application/json",
                        },
                    }
                );

                if (!res.ok) {
                    throw new Error("Address search failed.");
                }

                const data = await res.json();

                setSuggestions(
                    (data || []).map((item) => ({
                        id: item.place_id,
                        lat: Number(item.lat),
                        lng: Number(item.lon),
                        main_text:
                            item.name ||
                            item.address?.road ||
                            item.address?.suburb ||
                            item.address?.city ||
                            item.display_name,
                        secondary_text: item.display_name,
                        description: item.display_name,
                    }))
                );

                setSuggestionsOpen(true);
            } catch (err) {
                if (err.name !== "AbortError") {
                    console.error("Address search failed", err);
                    setSuggestions([]);
                    setSuggestionsOpen(false);
                }
            } finally {
                setSearching(false);
            }
        }, SEARCH_DEBOUNCE_MS);

        return () => {
            clearTimeout(searchDebounceRef.current);
        };
    }, [searchQuery, order?.restaurant?.latitude, order?.restaurant?.longitude]);

    // ── Map setup ─────────────────────────────────────────────
    useEffect(() => {
        if (!mapRef.current || mapInstance.current) return;

        const safeLat = Number.isFinite(Number(coords.lat)) ? Number(coords.lat) : 0;
        const safeLng = Number.isFinite(Number(coords.lng)) ? Number(coords.lng) : 0;

        const map = L.map(mapRef.current).setView([safeLat, safeLng], DEFAULT_ZOOM);

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
            attribution: "&copy; OpenStreetMap contributors",
        }).addTo(map);

        const marker = L.marker([safeLat, safeLng], {
            draggable: true,
            icon: dragPinIcon,
        }).addTo(map);

        const handleMove = (lat, lng) => {
            const nextLat = Number(lat);
            const nextLng = Number(lng);

            if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) return;

            setSelectedSlot(null);
            setFastExpressPicked(false);
            setCoords({ lat: nextLat, lng: nextLng });
        };

        marker.on("dragend", () => {
            const { lat, lng } = marker.getLatLng();
            handleMove(lat, lng);
        });

        map.on("click", (e) => {
            marker.setLatLng(e.latlng);
            handleMove(e.latlng.lat, e.latlng.lng);
        });

        mapInstance.current = map;
        markerRef.current = marker;

        const invalidateTimer = setTimeout(() => {
            if (mapInstance.current && map._loaded) {
                map.invalidateSize();
            }
        }, 100);

        return () => {
            clearTimeout(invalidateTimer);
            map.remove();
            mapInstance.current = null;
            markerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Debounced address/pricing check whenever the pin moves ──
    useEffect(() => {
        if (!order?.id) return;

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        const lat = Number(coords.lat);
        const lng = Number(coords.lng);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            setChecking(false);
            setLocationInfo(null);
            return;
        }

        const seq = ++requestSeq.current;
        setChecking(true);

        debounceRef.current = setTimeout(async () => {
            try {
                const res = await checkOrderAddressLocation(order.id, {
                    latitude: lat,
                    longitude: lng,
                });

                if (seq === requestSeq.current) {
                    setLocationInfo(res?.data);
                }
            } catch (err) {
                if (seq === requestSeq.current) {
                    showToast?.(
                        "error",
                        err?.response?.data?.message || "Failed to check this location."
                    );
                    setLocationInfo(null);
                }
            } finally {
                if (seq === requestSeq.current) {
                    setChecking(false);
                }
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coords.lat, coords.lng, order?.id]);

    useEffect(() => {
        if (locationInfo?.address?.formatted_address) {
            setAddressText(locationInfo.address.formatted_address);
        }
    }, [locationInfo?.address?.formatted_address]);

    const moveToLocation = (lat, lng, zoom = 17) => {
        const nextLat = Number(lat);
        const nextLng = Number(lng);

        if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) {
            showToast?.("error", "Invalid selected location.");
            return;
        }

        if (markerRef.current) {
            markerRef.current.setLatLng([nextLat, nextLng]);
        }

        if (mapInstance.current) {
            mapInstance.current.setView([nextLat, nextLng], zoom);
        }

        setSelectedSlot(null);
        setFastExpressPicked(false);
        setCoords({ lat: nextLat, lng: nextLng });
    };

    const handleSelectSuggestion = (suggestion) => {
        setResolvingPlace(true);

        try {
            setSuggestionsOpen(false);
            setSearchQuery(suggestion.description || "");
            setAddressText(suggestion.description || "");
            moveToLocation(suggestion.lat, suggestion.lng);
        } finally {
            setResolvingPlace(false);
        }
    };

    const clearSearch = () => {
        setSearchQuery("");
        setSuggestions([]);
        setSuggestionsOpen(false);
    };

    const pricing = locationInfo?.pricing;

    const handleConfirm = async (fastExpress) => {
        if (!addressText.trim()) {
            showToast?.("error", "Please enter the delivery address.");
            return;
        }

        if (!fastExpress && locationInfo?.slot_required && !selectedSlot) {
            showToast?.("error", "Please select a delivery slot.");
            return;
        }

        const lat = Number(coords.lat);
        const lng = Number(coords.lng);

        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            showToast?.("error", "Invalid delivery location.");
            return;
        }

        setSaving(true);

        try {
            const res = await updateOrderDeliveryAddress(order.id, {
                latitude: lat,
                longitude: lng,
                address: addressText.trim(),
                is_fast_express: fastExpress,
                delivery_slot_id: fastExpress ? null : selectedSlot?.id,
            });

            showToast?.("success", res?.message || "Address updated.");
            await onUpdated?.();
            onClose?.();
        } catch (err) {
            showToast?.(
                "error",
                err?.response?.data?.message || "Failed to update address."
            );
        } finally {
            setSaving(false);
        }
    };

    const PricingPanel = ({ label, data, isActive }) => {
        if (!data) return null;

        const diff = Number(data.total_difference ?? 0);

        return (
            <div className={`addr-pricing${isActive ? " is-active" : ""}`}>
                <div className="addr-pricing__label">{label}</div>

                <div className="addr-pricing__row">
                    <span>Delivery Charge</span>
                    <span>{fmt(data.delivery_charge)}</span>
                </div>

                {Number(data.coupon_savings ?? 0) > 0 && (
                    <div className="addr-pricing__row addr-pricing__row--coupon">
                        <span>
                            <Tag size={11} /> Coupon ({data.coupon?.code})
                        </span>
                        <span>-{fmt(data.coupon_savings)}</span>
                    </div>
                )}

                <div className="addr-pricing__row addr-pricing__row--total">
                    <span>New Order Total</span>
                    <span>{fmt(data.new_total)}</span>
                </div>

                <div
                    className={`addr-pricing__diff ${
                        diff > 0 ? "up" : diff < 0 ? "down" : ""
                    }`}
                >
                    {diff === 0
                        ? "No change vs current total"
                        : `${diff > 0 ? "+" : ""}${fmt(diff)} vs current total`}
                </div>
            </div>
        );
    };

    return (
        <div className="modal-overlay addr-modal-overlay" onClick={onClose}>
            <div
                className="modal-box addr-modal-box"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-header">
                    <h3 className="modal-title">Change Delivery Address</h3>
                    <button
                        className="modal-close"
                        onClick={onClose}
                        disabled={saving}
                    >
                        ✕
                    </button>
                </div>

                <div className="modal-body addr-modal-body">
                    <div className="addr-layout">
                        <div className="addr-layout__map">
                            <div className="addr-map-wrap">
                                <div className="addr-search addr-search--over-map" ref={searchWrapRef}>
                                    <div className="addr-search__input-wrap">
                                        <Search size={15} className="addr-search__icon"/>

                                        <input
                                            type="text"
                                            className="addr-search__input"
                                            placeholder="Search for an address or place…"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            onFocus={() => {
                                                if (suggestions.length) {
                                                    setSuggestionsOpen(true);
                                                }
                                            }}
                                        />

                                        {searching && (
                                            <div className="dp-loader addr-search__loader">
                                                <span className="dp-loader-dot"/>
                                                <span className="dp-loader-dot"/>
                                                <span className="dp-loader-dot"/>
                                            </div>
                                        )}

                                        {!searching && searchQuery && (
                                            <button
                                                type="button"
                                                className="addr-search__clear"
                                                onClick={clearSearch}
                                                aria-label="Clear search"
                                            >
                                                <XIcon size={14}/>
                                            </button>
                                        )}
                                    </div>

                                    {suggestionsOpen && suggestions.length > 0 && (
                                        <div className="addr-search__dropdown">
                                            {suggestions.map((s) => (
                                                <button
                                                    type="button"
                                                    key={s.id}
                                                    className="addr-search__suggestion"
                                                    disabled={resolvingPlace}
                                                    onClick={() => handleSelectSuggestion(s)}
                                                >
                                                    <MapPin
                                                        size={13}
                                                        className="addr-search__suggestion-icon"
                                                    />

                                                    <span className="addr-search__suggestion-text">
                            <span className="addr-search__suggestion-main">
                                {s.main_text}
                            </span>

                                                        {s.secondary_text && (
                                                            <span className="addr-search__suggestion-secondary">
                                    {s.secondary_text}
                                </span>
                                                        )}
                        </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div ref={mapRef} className="addr-map"/>
                            </div>

                            <div
                                className="modal-section-label"
                                style={{marginBottom: 6, marginTop: 10}}
                            >
                                Search address, drag the pin, or click anywhere to move the delivery point
                            </div>

                            <textarea
                                className="modal-textarea"
                                placeholder="Full delivery address…"
                                rows={2}
                                value={addressText}
                                onChange={(e) => setAddressText(e.target.value)}
                                style={{marginTop: 12}}
                            />
                        </div>

                        <div className="addr-layout__side">
                            {checking && (
                                <div className="addr-checking">
                                    <div className="dp-loader">
                                        <span className="dp-loader-dot"/>
                                        <span className="dp-loader-dot"/>
                                        <span className="dp-loader-dot"/>
                                    </div>
                                    Recalculating for this location…
                                </div>
                            )}

                            {!checking && locationInfo && (
                                <>
                                    <div className="addr-result__distance">
                                        <MapPin size={14}/> {locationInfo.distance} km from
                                        restaurant
                                    </div>

                                    {locationInfo.slot_required && (
                                        <>
                                            <div
                                                className="modal-section-label"
                                                style={{marginTop: 10}}
                                            >
                                                Delivery slots available for this address
                                            </div>

                                            <div className="modal-reasons">
                                                {locationInfo.slots?.map((slot) => (
                                                    <button
                                                        key={slot.id}
                                                        className={`modal-reason-chip${
                                                            selectedSlot?.id === slot.id &&
                                                            !fastExpressPicked
                                                                ? " is-active"
                                                                : ""
                                                        }`}
                                                        onClick={() => {
                                                            setSelectedSlot(slot);
                                                            setFastExpressPicked(false);
                                                        }}
                                                    >
                                                        {slot.name} ({slot.start_time}–{slot.end_time})
                                                    </button>
                                                ))}
                                            </div>

                                            <PricingPanel
                                                label="Slot-based delivery"
                                                data={pricing?.slot_based}
                                                isActive={!fastExpressPicked}
                                            />

                                            <button
                                                className="action-btn action-btn--accept"
                                                disabled={!selectedSlot || fastExpressPicked || saving}
                                                onClick={() => handleConfirm(false)}
                                            >
                                                {saving && !fastExpressPicked
                                                    ? "Saving…"
                                                    : "Confirm Slot & Update Address"}
                                            </button>

                                            <div className="addr-or">or</div>
                                        </>
                                    )}

                                    <PricingPanel
                                        label="Fast Express delivery"
                                        data={pricing?.fast_express}
                                        isActive={
                                            fastExpressPicked || !locationInfo.slot_required
                                        }
                                    />

                                    <button
                                        className={
                                            locationInfo.slot_required
                                                ? "btn"
                                                : "action-btn action-btn--accept"
                                        }
                                        style={
                                            locationInfo.slot_required
                                                ? {
                                                    width: "100%",
                                                    justifyContent: "center",
                                                }
                                                : undefined
                                        }
                                        disabled={saving}
                                        onClick={() => {
                                            setFastExpressPicked(true);
                                            handleConfirm(true);
                                        }}
                                    >
                                        {saving &&
                                        (fastExpressPicked || !locationInfo.slot_required)
                                            ? "Saving…"
                                            : locationInfo.slot_required
                                                ? "Send as Fast Express instead"
                                                : "Confirm Address (Fast Express)"}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChangeAddressModal;